import * as Sentry from "@sentry/react";
import { type InternalAxiosRequestConfig } from "axios";
import { api, apiV3 } from "@/api";
import {
  type ICreateCustomerRequest,
  type ICreateCustomerWithPermissionsRequest,
  type ICustomerDetails,
  type ICustomerMetadata,
  type ICustomerRemoveReason,
  type IDeleteCustomerWithPermissionsRequest,
  type IGetSignUpPrefillDataRequest,
  type IGetSignUpPrefillDataResponse,
  type IRemoveCustomerRequest,
  type IUpdateAppleConnectionRequest,
  type IUpdateCustomerDetailsRequest,
  type IUpdateFacebookConnectionRequest,
} from "./types";

export const getCustomerDetails = async () => {
  const response = await api.get<ICustomerDetails>("/customers/current");

  Sentry.setUser({ id: response.data.accountId });

  return response.data;
};

export const getCustomerMetadata = async () =>
  (
    await api.get<ICustomerMetadata>("/customers/metadata", {
      authorized: true,
    } as InternalAxiosRequestConfig)
  ).data;

export const updateFacebookConnectionRequest = async (
  data: IUpdateFacebookConnectionRequest,
) => {
  await api.put<void>("/customers/facebook", data);
};

export const updateAppleConnectionRequest = async (
  data: IUpdateAppleConnectionRequest,
) => {
  await api.put<void>("/customers/apple", data);
};

export const updateCustomerDetails = (data: IUpdateCustomerDetailsRequest) =>
  api.put<string>("/customers", data).then((r) => r.data);

export const createCustomer = async (data: ICreateCustomerRequest) =>
  (await api.post<string>("/customers", data, { params: { e: data.e } })).data;

export const removeCustomer = async (data: IRemoveCustomerRequest) =>
  apiV3.post<string>("/customers/block", data).then((r) => r.data);

export const getCustomerRemoveReasons = async () =>
  apiV3
    .get<{
      customerStateChangingReasons: ICustomerRemoveReason[];
    }>("/customers/block/reasons")
    .then((r) => r.data?.customerStateChangingReasons ?? []);

export const createCustomerWithPermissions = (
  data: ICreateCustomerWithPermissionsRequest,
) => {
  const { jointBillServiceId, ...restData } = data;
  return api
    .post<string>("/customers/customer-with-permissions", restData, {
      headers: {
        jointBillServiceId,
      },
    })
    .then((r) => r.data);
};

export const deleteCustomerWithPermissions = async (
  data: IDeleteCustomerWithPermissionsRequest,
) => {
  const { jointBillServiceId, customerId } = data;

  return await api.delete<void>(
    `/joint-bill-services/customers/${customerId}`,
    {
      headers: {
        jointBillServiceId,
      },
    },
  );
};

export const resetCustomerPassword = async (data: {
  newPassword: string;
  encryptedToken: string;
}) =>
  api.put<string>(`/customers/reset-password`, {
    newPassword: data.newPassword,
    encryptedToken: encodeURIComponent(data.encryptedToken),
  });

export const updateCustomerCultureId = async (cultureId: string) =>
  api.put("/customers/culture", { cultureId });

export const activateCustomer = async () => api.put("/customers/activate");

export const getSignUpPrefillData = async (
  data: IGetSignUpPrefillDataRequest,
) =>
  api
    .get<IGetSignUpPrefillDataResponse>("/customers/registration/prefill", {
      params: {
        e: data.e,
      },
    })
    .then((r) => r.data);
